/* Dhrohar 0.6.3: image loading that keeps the main photo first.
   1. Bag images (the empty-bag picks and line photos in the bag drawer) carry data-dh-src / data-dh-srcset and
      load only when the bag opens, or when the shopper heads for the bag icon. They no longer load on every page.
   2. Card hover photos ([data-dh-hover]) load only on screens with a mouse, after the page has loaded, as their
      card nears the viewport, or at once on pointer-enter. Phones never download them.
   3. Swatch dots with a photo ([data-dh-bg]) get their background only when they near the viewport.
   4. Lazy photos that are still downloading fade in softly instead of painting line by line. No fade when the
      photo is already there, and none with reduced motion.
   Everything degrades to plain images: with this script missing, bag and hover photos simply do not show. */
(() => {
  if (window.DhroharMedia) return;
  window.DhroharMedia = true;

  const hasIO = 'IntersectionObserver' in window;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hydrate = (img, priority) => {
    if (!img || !(img.dataset.dhSrc || img.dataset.dhSrcset)) return;
    if (priority) img.fetchPriority = priority;
    if (img.dataset.dhSrcset) {
      img.srcset = img.dataset.dhSrcset;
      img.removeAttribute('data-dh-srcset');
    }
    if (img.dataset.dhSrc) {
      img.src = img.dataset.dhSrc;
      img.removeAttribute('data-dh-src');
    }
  };

  /* ---------- 1. bag drawer ---------- */
  const drawer = () => document.querySelector('cart-drawer');
  const hydrateBag = () => drawer()?.querySelectorAll('img[data-dh-src], img[data-dh-srcset]').forEach((img) => hydrate(img, 'high'));
  const watchBag = () => {
    const d = drawer();
    if (!d || d.dataset.dhMediaWatch) return;
    d.dataset.dhMediaWatch = '1';
    if (d.classList.contains('active')) hydrateBag();
    // opened, or re-rendered after a bag change (the shopper is using the bag): load its photos
    new MutationObserver(() => {
      if (d.classList.contains('active') || d.dataset.dhMediaWarm) hydrateBag();
    }).observe(d, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
  };
  // intent: the shopper moves towards / touches / focuses the bag icon
  const warmBag = (event) => {
    if (!event.target.closest || !event.target.closest('#cart-icon-bubble')) return;
    const d = drawer();
    if (d) d.dataset.dhMediaWarm = '1';
    hydrateBag();
  };
  ['pointerover', 'touchstart', 'focusin'].forEach((type) => document.addEventListener(type, warmBag, { passive: true, capture: true }));

  /* ---------- 2. card hover photos ---------- */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  let hoverIO = null;
  let hoverReady = false; // nothing before the page has loaded; startHover() scans the whole page then
  const watchHover = (root) => {
    if (!canHover || !hoverReady) return;
    root.querySelectorAll('img[data-dh-hover][data-dh-srcset]').forEach((img) => {
      if (img.dataset.dhHoverWatch) return;
      img.dataset.dhHoverWatch = '1';
      if (hoverIO) hoverIO.observe(img);
      else hydrate(img, 'low');
    });
  };
  if (canHover) {
    document.addEventListener(
      'pointerover',
      (event) => {
        const card = event.target.closest && event.target.closest('.card-wrapper, .product-card-wrapper');
        if (card) card.querySelectorAll('img[data-dh-hover][data-dh-srcset]').forEach((img) => hydrate(img, 'high'));
      },
      { passive: true }
    );
    const startHover = () => {
      hoverReady = true;
      if (hasIO) {
        hoverIO = new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              hydrate(entry.target, 'low');
              hoverIO.unobserve(entry.target);
            }),
          { rootMargin: '200px 0px' }
        );
      }
      watchHover(document);
    };
    const afterLoad = () => ('requestIdleCallback' in window ? requestIdleCallback(startHover, { timeout: 2000 }) : setTimeout(startHover, 300));
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  }

  /* ---------- 3. swatch photo dots ---------- */
  const applyBg = (el) => {
    if (!el.dataset.dhBg) return;
    el.style.backgroundImage = el.dataset.dhBg;
    el.removeAttribute('data-dh-bg');
  };
  const bgIO = hasIO
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            applyBg(entry.target);
            bgIO.unobserve(entry.target);
          }),
        { rootMargin: '300px 0px' }
      )
    : null;
  const watchBg = (root) => root.querySelectorAll('[data-dh-bg]').forEach((el) => (bgIO ? bgIO.observe(el) : applyBg(el)));

  /* ---------- 4. soft fade for lazy photos still downloading ---------- */
  const FADE_SCOPE = '.card__media img, .product__media img, .dh-slide__photo img, .dh-cat img, .dh-recent img, .dh-pick__media img';
  const fade = (root) => {
    if (reduceMotion) return;
    root.querySelectorAll(FADE_SCOPE).forEach((img) => {
      if (img.dataset.dhFade || img.complete || img.loading !== 'lazy' || img.hasAttribute('data-dh-hover')) return;
      img.dataset.dhFade = '1';
      img.classList.add('dh-fade', 'dh-fade--wait');
      const done = () => img.classList.remove('dh-fade--wait');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      if (img.complete) done();
    });
  };

  /* ---------- wiring: now, and for content added later (filters, recently viewed, bag re-renders) ---------- */
  const scan = (root) => {
    watchBg(root);
    watchHover(root);
    fade(root);
  };
  const start = () => {
    watchBag();
    scan(document);
    new MutationObserver((records) => {
      records.forEach((r) => r.addedNodes.forEach((n) => n.nodeType === 1 && scan(n)));
      watchBag();
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
